import { useState, useEffect, useRef, useCallback } from 'react';
import { downloadImage } from '../../../utils/upload-image';
import { PLACEHOLDER_PROFILE_NO_PHOTO } from '../../../utils/placeholders';
import { can } from '../../../utils/permissions';
import { hasAlturaMansFeature } from '../../../utils/colla-features';
import ActionsModal from './ActionsModal';
import { normalizeNumberInputValue, parseNullableInteger } from '../userFieldUtils';

function CastellerRow({
    user,
    onFieldChange,
    deleteUser,
    onPhotoClick,
    profilePicOverride,
    show_popup,
    userInfo,
    COLLA,
    socket,
    sortBy,
    showEtiquetes,
    allEtiquetes,
    userEtiquetes,
    onEtiquetaToggle
}) {
    const [profilePic, setProfilePic] = useState(null);
    const [showActionsModal, setShowActionsModal] = useState(false);
    const [shouldLoadPhoto, setShouldLoadPhoto] = useState(false);
    const rowRef = useRef(null);

    // Estat local pels camps de text per evitar que els socket updates sobreescriguin mentre s'edita
    const [localValues, setLocalValues] = useState({
        mote: user.mote || '',
        nom: user.nom || '',
        'primer-cognom': user['primer-cognom'] || '',
        'segon-cognom': user['segon-cognom'] || '',
        altura: normalizeNumberInputValue(user.altura),
        altura_mans: normalizeNumberInputValue(user.altura_mans),
    });

    // Timers de debounce per cada camp
    const debounceTimers = useRef({});
    // Marca quins camps s'estan editant
    const editingFields = useRef({});

    // Actualitza l'estat local des de les props NOMÉS si el camp no s'està editant
    useEffect(() => {
        setLocalValues(prev => {
            const newValues = { ...prev };
            const fieldsToCheck = ['mote', 'nom', 'primer-cognom', 'segon-cognom', 'altura', 'altura_mans'];
            
            fieldsToCheck.forEach(field => {
                // Només actualitzar si no estem editant aquest camp
                if (!editingFields.current[field]) {
                    const propValue = user[field];
                    if (field === 'altura' || field === 'altura_mans') {
                        newValues[field] = normalizeNumberInputValue(propValue);
                    } else {
                        newValues[field] = propValue !== null && propValue !== undefined ? propValue : '';
                    }
                }
            });
            
            return newValues;
        });
    }, [user.id, user.mote, user.nom, user['primer-cognom'], user['segon-cognom'], user.altura, user.altura_mans]);

    // Handler per camps de text amb debounce local
    const handleTextChange = useCallback((field, value) => {
        // Actualitzar l'estat local immediatament
        setLocalValues(prev => ({ ...prev, [field]: value }));

        // Marcar el camp com a "en edició"
        editingFields.current[field] = true;

        // Cancel·lar el timer anterior
        if (debounceTimers.current[field]) {
            clearTimeout(debounceTimers.current[field]);
        }

        // Crear nou timer
        debounceTimers.current[field] = setTimeout(() => {
            // Enviar el canvi al servidor
            const changes = {};
            changes[field] = (field === 'altura' || field === 'altura_mans')
                ? parseNullableInteger(value)
                : value;
            onFieldChange(user.id, field, changes[field]);

            // Marcar el camp com a "no en edició" després d'un petit delay
            // per permetre que el socket update arribi
            setTimeout(() => {
                editingFields.current[field] = false;
            }, 1000);

            delete debounceTimers.current[field];
        }, 500);
    }, [user.id, onFieldChange]);

    // Netejar timers quan el component es desmunta
    useEffect(() => {
        return () => {
            Object.values(debounceTimers.current).forEach(timer => clearTimeout(timer));
        };
    }, []);

    // Precarrega les fotos amb marge dins del contenidor que fa scroll.
    useEffect(() => {
        const row = rowRef.current;
        if (!row) return;

        const observer = new IntersectionObserver(
            (entries) => {
                entries.forEach(entry => {
                    if (entry.isIntersecting) {
                        setShouldLoadPhoto(true);
                        observer.disconnect();
                    }
                });
            },
            {
                // El marge de la finestra queda retallat per l'overflow de la taula.
                root: row.closest('.castellers-table-container'),
                rootMargin: '1000px 0px',
                threshold: 0.01
            }
        );

        observer.observe(row);

        return () => {
            observer.disconnect();
        };
    }, []);

    // Carrega la foto quan la fila s'apropa a la zona visible.
    useEffect(() => {
        if (shouldLoadPhoto && profilePic === null) {
            downloadImage(user.id, COLLA)
                .then(base64 => setProfilePic(base64))
                .catch(() => setProfilePic(false));
        }
    }, [shouldLoadPhoto, user.id, COLLA, profilePic]);

    // Si una foto s'ha actualitzat des del modal, sincronitzem la miniatura
    useEffect(() => {
        if (profilePicOverride !== undefined) {
            setProfilePic(profilePicOverride);
        }
    }, [profilePicOverride]);

    return (
        <tr ref={rowRef} className={`casteller-row${user.hidden === 1 ? ' casteller-hidden' : ''}`}>
            {/* Foto petita clicable */}
            <td className="photo-cell">
                <div className="photo-container">
                    {profilePic === null ? (
                        <div className="photo-loading-mini">...</div>
                    ) : (
                        <img
                            src={profilePic || PLACEHOLDER_PROFILE_NO_PHOTO}
                            onClick={() => onPhotoClick(profilePic)}
                            className="photo-thumbnail"
                            alt={`Foto de ${user.mote}`}
                        />
                    )}
                    {user.hidden === 1 ? (
                        <div className="hidden-overlay" title="Casteller amagat">
                            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"/>
                                <line x1="1" y1="1" x2="23" y2="23"/>
                            </svg>
                        </div>
                    ) : null}
                </div>
            </td>

            {/* Mote */}
            <td className={sortBy === 'mote' ? 'sorted-column' : ''}>
                <input
                    type="text"
                    value={localValues.mote}
                    onChange={(e) => handleTextChange('mote', e.target.value)}
                    className="inline-input"
                    placeholder="Sobrenom"
                />
            </td>

            {/* Nom */}
            <td className={sortBy === 'nom' ? 'sorted-column' : ''}>
                <input
                    type="text"
                    value={localValues.nom}
                    onChange={(e) => handleTextChange('nom', e.target.value)}
                    className="inline-input"
                    placeholder="Nom"
                />
            </td>

            {/* 1r Cognom */}
            <td className={sortBy === 'primer-cognom' ? 'sorted-column' : ''}>
                <input
                    type="text"
                    value={localValues['primer-cognom']}
                    onChange={(e) => handleTextChange('primer-cognom', e.target.value)}
                    className="inline-input"
                    placeholder="1r cognom"
                />
            </td>

            {/* 2n Cognom */}
            <td className={sortBy === 'segon-cognom' ? 'sorted-column' : ''}>
                <input
                    type="text"
                    value={localValues['segon-cognom']}
                    onChange={(e) => handleTextChange('segon-cognom', e.target.value)}
                    className="inline-input"
                    placeholder="2n cognom"
                />
            </td>

            {/* Altura */}
            <td className={sortBy === 'altura' ? 'sorted-column' : ''}>
                <input
                    type="number"
                    value={localValues.altura}
                    onChange={(e) => handleTextChange('altura', e.target.value)}
                    onWheel={(e) => e.target.blur()}
                    className="inline-input inline-input-number"
                    placeholder="cm"
                    min="0"
                />
            </td>

            {/* Altura mans (només per colles que ho utilitzen) */}
            {hasAlturaMansFeature() ? (
                <td className={sortBy === 'altura_mans' ? 'sorted-column' : ''}>
                    <input
                        type="number"
                        value={localValues.altura_mans}
                        onChange={(e) => handleTextChange('altura_mans', e.target.value)}
                        onWheel={(e) => e.target.blur()}
                        className="inline-input inline-input-number"
                        placeholder="cm"
                        min="0"
                    />
                </td>
            ) : null}

            {/* Tècnica */}
            <td className={sortBy === 'es_tecnica' ? 'sorted-column' : ''}>
                <select
                    value={user.es_tecnica || 0}
                    onChange={(e) => onFieldChange(user.id, 'tecnica', parseInt(e.target.value))}
                    className="inline-select"
                    style={{ minWidth: 65 }}
                    disabled={!can(userInfo, 'ASSIGN_TECNICA')}
                >
                    <option value="0">No</option>
                    {(can(userInfo, 'ASSIGN_TECNICA') || user.es_tecnica === 1) ? <option value="1">Membre</option> : null}
                    {(can(userInfo, 'ASSIGN_HEAD_TECNICA') || user.es_tecnica === 2) ? <option value="2">Cap de</option> : null}
                </select>
            </td>

            {/* Junta */}
            <td className={sortBy === 'es_junta' ? 'sorted-column' : ''}>
                <select
                    value={user.es_junta || 0}
                    onChange={(e) => onFieldChange(user.id, 'junta', parseInt(e.target.value))}
                    className="inline-select"
                    style={{ minWidth: 65 }}
                    disabled={!can(userInfo, 'ASSIGN_JUNTA')}
                >
                    <option value="0">No</option>
                    {(can(userInfo, 'ASSIGN_JUNTA') || user.es_junta === 1) ? <option value="1">Membre</option> : null}
                    {(can(userInfo, 'ASSIGN_HEAD_JUNTA') || user.es_junta === 2) ? <option value="2">Cap de</option> : null}
                </select>
            </td>

            {/* Etiquetes */}
            {showEtiquetes && allEtiquetes?.map(etiqueta => (
                <td key={etiqueta.id} className={`checkbox-cell etiqueta-checkbox-cell${sortBy === `etiqueta_${etiqueta.id}` ? ' sorted-column' : ''}`}>
                    <input
                        type="checkbox"
                        checked={userEtiquetes?.includes(etiqueta.id)}
                        onChange={(e) => onEtiquetaToggle(user.id, etiqueta.id, e.target.checked)}
                    />
                </td>
            ))}

            {/* Canalla */}
            <td className={`checkbox-cell${sortBy === 'canalla' ? ' sorted-column' : ''}`}>
                <input
                    type="checkbox"
                    checked={user.canalla === 1}
                    onChange={(e) => onFieldChange(user.id, 'canalla', e.target.checked ? 1 : 0)}
                />
            </td>

            {/* Músic */}
            <td className={`checkbox-cell${sortBy === 'music' ? ' sorted-column' : ''}`}>
                <input
                    type="checkbox"
                    checked={user.music === 1}
                    onChange={(e) => onFieldChange(user.id, 'music', e.target.checked ? 1 : 0)}
                />
            </td>

            {/* Lesionat */}
            <td className={`checkbox-cell${sortBy === 'lesionat' ? ' sorted-column' : ''}`}>
                <input
                    type="checkbox"
                    checked={user.lesionat === 1}
                    onChange={(e) => onFieldChange(user.id, 'lesionat', e.target.checked ? 1 : 0)}
                />
            </td>

            {/* Novell */}
            <td className={`checkbox-cell${sortBy === 'novell' ? ' sorted-column' : ''}`}>
                <input
                    type="checkbox"
                    checked={user.novell === 1}
                    onChange={(e) => onFieldChange(user.id, 'novell', e.target.checked ? 1 : 0)}
                />
            </td>

            {/* Soci (informatiu, gestionat automàticament per quotes) */}
            <td className={`checkbox-cell${sortBy === 'soci' ? ' sorted-column' : ''}`} title="S'actualitza automàticament segons les quotes">
                <input
                    type="checkbox"
                    checked={user.soci === 1}
                    disabled
                    readOnly
                />
            </td>

            {/* Casteller */}
            <td className={`checkbox-cell${sortBy === 'casteller' ? ' sorted-column' : ''}`}>
                <input
                    type="checkbox"
                    checked={user.casteller === 1}
                    onChange={(e) => onFieldChange(user.id, 'casteller', e.target.checked ? 1 : 0)}
                />
            </td>

            {/* Accions */}
            <td className="castellers-actions-cell">
                <div className="actions-wrapper">
                    <button
                        onClick={() => setShowActionsModal(true)}
                        className="actions-menu-button"
                        title="Més accions"
                    >
                        ⚙️
                    </button>
                </div>
            </td>

            {showActionsModal && (
                <ActionsModal
                    user={user}
                    onClose={() => setShowActionsModal(false)}
                    onFieldChange={onFieldChange}
                    deleteUser={deleteUser}
                    socket={socket}
                    userInfo={userInfo}
                    COLLA={COLLA}
                />
            )}
        </tr>
    );
}

export default CastellerRow;
